/**
 * Dropdown — substitui <select> nativo com visual coeso ao design Pagmus V2.
 *
 * Uso:
 *   <Dropdown
 *     value={value}
 *     onChange={setValue}
 *     options={[
 *       { value: 'pix', label: 'PIX', icon: 'mdi:qrcode', secondary: '0% taxa' },
 *       { value: 'cc',  label: 'Cartão', icon: 'mdi:credit-card-outline', disabled: false },
 *     ]}
 *     placeholder="Selecione..."
 *     disabled={false}
 *     compact={false}    // padding menor (use em drawers)
 *   />
 *
 * options[].value pode ser string ou número.
 * options[].icon é opcional (mdi:* via Iconify).
 * options[].secondary é texto auxiliar mostrado à direita do label.
 * options[].disabled desabilita o item.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import './Dropdown.css';

export const Dropdown = ({
  value,
  onChange,
  options = [],
  placeholder = 'Selecione...',
  disabled = false,
  compact = false,
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const wrapRef = useRef(null);
  const buttonRef = useRef(null);

  // Fecha ao clicar fora
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Decide se abre pra cima ou pra baixo dependendo do espaço
  useEffect(() => {
    if (!open || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    setOpenUp(spaceBelow < 220 && spaceAbove > spaceBelow);
  }, [open]);

  const selected = options.find((o) => String(o.value) === String(value));

  const handleSelect = (opt) => {
    if (opt.disabled) return;
    onChange?.(opt.value);
    setOpen(false);
  };

  return (
    <div
      ref={wrapRef}
      className={`dd ${open ? 'is-open' : ''} ${compact ? 'dd--compact' : ''} ${className}`}
    >
      <button
        ref={buttonRef}
        type="button"
        className="dd__button"
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {selected ? (
          <span className="dd__value-wrap">
            {selected.icon && (
              <Icon className="dd__value-icon" icon={selected.icon} width="16" />
            )}
            <span className="dd__value-label">{selected.label}</span>
            {selected.secondary && (
              <span className="dd__value-secondary">{selected.secondary}</span>
            )}
          </span>
        ) : (
          <span className="dd__value-wrap">
            <span className="dd__value-label dd__placeholder">{placeholder}</span>
          </span>
        )}
        <Icon className="dd__icon" icon="mdi:chevron-down" width="18" />
      </button>

      {open && (
        <div
          className={`dd__menu ${openUp ? 'dd__menu--up' : ''}`}
          role="listbox"
        >
          {options.length === 0 ? (
            <div className="dd__empty">Nenhuma opção disponível</div>
          ) : (
            options.map((opt) => {
              const isSelected = String(opt.value) === String(value);
              return (
                <button
                  key={String(opt.value)}
                  type="button"
                  className={`dd__option ${isSelected ? 'is-selected' : ''}`}
                  onClick={() => handleSelect(opt)}
                  disabled={opt.disabled}
                  role="option"
                  aria-selected={isSelected}
                >
                  {opt.icon && (
                    <Icon className="dd__option-icon" icon={opt.icon} width="16" />
                  )}
                  <span className="dd__option-label">{opt.label}</span>
                  {opt.secondary && (
                    <span className="dd__option-secondary">{opt.secondary}</span>
                  )}
                  <Icon className="dd__option-check" icon="mdi:check" width="14" />
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default Dropdown;
